import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Key, Eye, EyeOff, Save, Loader2, ShieldCheck, Sparkles, Mail, CreditCard, BarChart3, Cloud, Globe, RefreshCw } from 'lucide-react';
import { apiKeysAPI } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const GROUP_META = {
  ai:        { label: 'AI / LLM',        icon: Sparkles,    desc: 'OpenAI and Anthropic keys for article generation & summarization' },
  oauth:     { label: 'OAuth Providers',  icon: ShieldCheck, desc: 'Google and GitHub SSO credentials' },
  email:     { label: 'Email Delivery',   icon: Mail,        desc: 'SendGrid or SMTP configuration for newsletters and transactional email' },
  payments:  { label: 'Payments',         icon: CreditCard,  desc: 'Stripe API key and subscription price IDs' },
  analytics: { label: 'Analytics',        icon: BarChart3,   desc: 'Google Analytics 4 Measurement Protocol' },
  services:  { label: 'External Services',icon: Cloud,       desc: 'Weather and other third-party service keys' },
  general:   { label: 'General',          icon: Globe,       desc: 'Frontend URL and other general configuration' },
};

export default function IntegrationsSetting() {
  const [keys, setKeys] = useState([]);
  const [edits, setEdits] = useState({});
  const [visible, setVisible] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const res = await apiKeysAPI.get();
      setKeys(res.data.keys || []);
    } catch {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (key, value) => {
    setEdits(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveKeys = async () => {
    const nonEmpty = Object.fromEntries(Object.entries(edits).filter(([_, v]) => v !== undefined));
    if (Object.keys(nonEmpty).length === 0) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await apiKeysAPI.update(nonEmpty);
      toast.success(`Updated ${Object.keys(nonEmpty).length} key(s)`);
      setEdits({});
      setVisible({});
      await loadKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save API keys');
    } finally {
      setSaving(false);
    }
  };

  const grouped = {};
  keys.forEach(k => {
    if (!grouped[k.group]) grouped[k.group] = [];
    grouped[k.group].push(k);
  });

  const hasChanges = Object.keys(edits).length > 0;

  return (
    <div className="space-y-6">
      {/* API Keys Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key className="w-5 h-5 text-[#B89B5E]" />
          <h3 className="text-sm font-semibold dark:text-white/90">API Keys & Integrations</h3>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadKeys} disabled={loading}>
            <RefreshCw className={cn('w-3.5 h-3.5 mr-1', loading && 'animate-spin')} /> Refresh
          </Button>
          {hasChanges && (
            <Button size="sm" onClick={handleSaveKeys} disabled={saving} className="bg-[#121316] dark:bg-white/10 text-white">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save {Object.keys(edits).length} Change{Object.keys(edits).length !== 1 ? 's' : ''}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-sm text-[#5B616E]">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading API keys…
        </div>
      ) : (
        Object.entries(GROUP_META).map(([groupId, meta]) => {
          const groupKeys = grouped[groupId];
          if (!groupKeys || groupKeys.length === 0) return null;
          const GroupIcon = meta.icon;
          return (
            <Card key={groupId} className="bg-white dark:bg-[#1A1D23] border-[#E7E0D6] dark:border-white/[0.06]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <GroupIcon className="w-4 h-4 text-[#B89B5E]" /> {meta.label}
                </CardTitle>
                <p className="text-xs text-[#5B616E] dark:text-white/40">{meta.desc}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {groupKeys.map(k => {
                  const isEditing = edits[k.key] !== undefined;
                  const showValue = visible[k.key];
                  return (
                    <div key={k.key} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Label className="text-xs font-medium dark:text-white/80">{k.label}</Label>
                          {k.is_set ? (
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-green-300 text-green-700 dark:border-green-700 dark:text-green-400">
                              {k.source === 'database' ? 'DB' : 'ENV'}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-[#E7E0D6] text-[#5B616E] dark:border-white/10 dark:text-white/30">
                              Not set
                            </Badge>
                          )}
                        </div>
                        <div className="relative">
                          <Input
                            type={showValue ? 'text' : 'password'}
                            placeholder={k.is_set ? k.masked_value : 'Enter value…'}
                            value={isEditing ? edits[k.key] : ''}
                            onChange={e => handleEdit(k.key, e.target.value)}
                            className="pr-10 text-xs h-9 font-mono"
                          />
                          <button
                            type="button"
                            onClick={() => setVisible(prev => ({ ...prev, [k.key]: !prev[k.key] }))}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#5B616E] hover:text-[#121316] dark:hover:text-white/80"
                          >
                            {showValue ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Bottom save bar */}
      {hasChanges && (
        <div className="sticky bottom-4 flex justify-end">
          <Button onClick={handleSaveKeys} disabled={saving} className="bg-[#121316] dark:bg-white/10 text-white shadow-lg">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save All Changes
          </Button>
        </div>
      )}
    </div>
  );
}
